// How hard Stockfish works on the analysis board — a Settings preference.
//
// Two things read this: the whole-game sweep that evaluates every position (and
// so decides how trustworthy the ?!/?/?? glyphs are), and the *continuous*
// search the analysis board keeps running on whatever position you are looking
// at. `unlimited` is what makes the second one interesting: the sweep still ends
// (it has to, or the glyphs never appear — see `SWEEP_DEPTH`), but the viewed
// position is then analysed with no depth limit until you navigate away.
//
// Client-only, persisted in localStorage, mirrored across views by a
// `analysisdepthchange` event — same shape as `game/premove.ts` and `theme.ts`.

import { t } from "../i18n/index.ts";
import { listenWhileMounted } from "../ui/lifecycle.ts";

export type DepthId = "fast" | "balanced" | "deep" | "unlimited";

export interface DepthLevel {
  id: DepthId;
  /** Target search depth, or 0 for "no limit — keep thinking". */
  depth: number;
}

export const DEPTH_LEVELS: DepthLevel[] = [
  { id: "fast", depth: 12 },
  { id: "balanced", depth: 16 },
  { id: "deep", depth: 20 },
  { id: "unlimited", depth: 0 },
];

const DEFAULT_ID: DepthId = "balanced";

/** Depth of the whole-game sweep when the user asked for no limit. The sweep has
 *  to terminate for the move judgements to appear at all, so it borrows the
 *  balanced depth; only the position in view thinks forever. */
export const SWEEP_DEPTH = 16;

const STORAGE_KEY = "chess:analysisDepth";
const CHANGE_EVENT = "analysisdepthchange";
let memory: DepthId | undefined;

const levelOf = (id: string | null): DepthLevel =>
  DEPTH_LEVELS.find((l) => l.id === id) ?? DEPTH_LEVELS.find((l) => l.id === DEFAULT_ID)!;

/** The stored (or default) level. Falls back to `balanced` on unknown/missing
 *  values and when storage is unavailable (private mode). */
export function analysisDepth(): DepthLevel {
  if (memory) return levelOf(memory);
  try {
    return levelOf(localStorage.getItem(STORAGE_KEY));
  } catch {
    return levelOf(null);
  }
}

/** The depth the whole-game sweep should use for `level`. */
export function sweepDepth(level: DepthLevel): number {
  return level.depth || SWEEP_DEPTH;
}

/** Persist + broadcast a new analysis depth. */
export function setAnalysisDepth(id: DepthId): void {
  memory = levelOf(id).id;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* non-fatal: the choice just won't survive a reload */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: id }));
  }
}

/** Subscribe to depth changes; returns an unsubscribe function. */
export function onAnalysisDepthChange(cb: (level: DepthLevel) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => cb(analysisDepth());
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/** The option label: "Deep · depth 20", or "Unlimited · keeps thinking". */
export function depthLabel(level: DepthLevel): string {
  const detail = level.depth ? t("depth.plies", { n: level.depth }) : t("depth.keepsThinking");
  return `${t(`depth.${level.id}`)} · ${detail}`;
}

/** A labelled `<select>` for the Settings panel (kept in sync via the event). */
export function analysisDepthSelect(): HTMLElement {
  const field = document.createElement("label");
  field.className = "field field-help";
  field.innerHTML =
    `<span>${t("settings.analysisDepth")}<small class="fine">${t("settings.analysisDepthHelp")}</small></span>` +
    `<select name="analysis-depth">` +
    DEPTH_LEVELS.map((l) => `<option value="${l.id}">${depthLabel(l)}</option>`).join("") +
    `</select>`;
  const select = field.querySelector("select")!;
  select.value = analysisDepth().id;
  select.addEventListener("change", () => setAnalysisDepth(select.value as DepthId));
  listenWhileMounted(field, CHANGE_EVENT, () => { select.value = analysisDepth().id; });
  return field;
}
